import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import { AddCollaboratorsPayload } from '../../../types/tree';
import { useForm, Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { List, ListItem, ListItemText, useTheme } from '@mui/material';
import { useAuth } from '../../../components/auth';

type AddCollaboratorsProps = {
  closeModal: () => void;
  onAddCollaborators: (payload: AddCollaboratorsPayload) => void;
  treeName: string;
};

export default function AddCollaborators({
  closeModal,
  onAddCollaborators,
  treeName,
}: AddCollaboratorsProps) {
  const { typography, palette } = useTheme();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string }>();

  const { currentUser } = useAuth();

  function handleOnAddCollaborators(data: any) {
    const payload: AddCollaboratorsPayload = {
      collaborators: [data.email],
      inviter_email: currentUser?.email || '',
      inviter_name: currentUser?.displayName || '',
      family_tree_name: treeName,
    };
    onAddCollaborators(payload);
  }
  return (
    <Box p={5}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
        <Typography
          sx={{
            fontWeight: typography.h5.fontWeight,
            fontSize: {
              xs: typography.h5.fontSize,
              md: typography.h3.fontSize,
            },
            color: palette.text.primary,
          }}
        >
          Add a collaborator
        </Typography>
        <IconButton onClick={closeModal}>
          <ClearIcon />
        </IconButton>
      </Box>
      <Box pt={1}>
        <Typography variant="body1">
          Enter the email address of the person you’d like to invite to
          collaborate on this family tree. Share the tree url link with them -
          navigate to family overview and you will find Copy tree URL.
        </Typography>
        <Box
          my={2}
          p={2}
          sx={{ bgcolor: 'background.default', borderRadius: 2 }}
        >
          <Typography
            variant="subtitle1"
            color="warning.main"
            fontWeight="bold"
            gutterBottom
          >
            Collaborator Permissions
          </Typography>

          <List dense>
            <ListItem>
              <ListItemText primary="Can view and edit the tree in the Tree tab (e.g. add members, edit member details, delete members)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Cannot edit family tree details in the Overview tab, add collaborators, or delete the tree." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Cannot view Stories or the Journeys section." />
            </ListItem>
          </List>
        </Box>
        <Box py={2}>
          <form onSubmit={handleSubmit(handleOnAddCollaborators)}>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: 'Invalid email address',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email ? errors.email.message : ''}
                />
              )}
            />
          </form>
        </Box>
      </Box>
      <Box
        py={2}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Button variant="outlined" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnAddCollaborators)}
        >
          Send Invitation
        </Button>
      </Box>
    </Box>
  );
}
